
import { useTheme } from '@/hooks/useTheme'
import { Button } from '@/components/ui/button'
import { Check, Palette, Moon, Sun, Sparkles } from 'lucide-react'
import { useState, useEffect } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu"

export function ThemeSwitcher() {
  const { currentTheme, setTheme: setCustomTheme, availableThemes } = useTheme()
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [isThemeOpen, setIsThemeOpen] = useState(false)

  useEffect(() => {
    setMounted(true)
    const saved = localStorage.getItem('dark-mode')
    const darkModeEnabled = saved ? JSON.parse(saved) : false
    
    setIsDarkMode(darkModeEnabled)
    document.documentElement.classList.toggle('dark', darkModeEnabled)
  }, [])

  const toggleDarkMode = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    localStorage.setItem('dark-mode', JSON.stringify(newMode))
    document.documentElement.classList.toggle('dark', newMode)
  }

  if (!mounted) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-9 w-9" />
        <div className="h-9 w-9" />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {/* Color Theme Selector */}
      {/* <DropdownMenu open={isThemeOpen} onOpenChange={setIsThemeOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="relative h-9 w-9 p-0 rounded-xl transition-all duration-300 hover:scale-110 group overflow-hidden border border-transparent hover:border-theme-primary/20"
          >
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-theme-gradient"
            />
            <div className="relative z-10">
              <Palette 
                className="h-4 w-4 transition-all duration-300 group-hover:rotate-12 text-theme-primary" 
              />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-60 border shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200"
          style={{ 
            borderColor: `rgba(var(--theme-primary), 0.3)`,
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
            backdropFilter: 'blur(12px)',
          }}
        >
          <DropdownMenuLabel 
            className="flex items-center gap-2 px-3 py-2.5"
            style={{
              color: isDarkMode ? '#ffffff' : '#1a1a1a'
            }}
          >
            <Sparkles className="h-4 w-4 text-theme-primary" />
            <span className="font-semibold">Color Theme</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator 
            style={{ 
              backgroundColor: isDarkMode 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)' 
            }} 
          />
          <div className="p-2 space-y-1">
            {Object.entries(availableThemes).map(([key, theme]) => {
              const isSelected = currentTheme === key
              
              return (
                <DropdownMenuItem
                  key={key}
                  onClick={() => {
                    setCustomTheme(key as keyof typeof availableThemes)
                    setTimeout(() => setIsThemeOpen(false), 150)
                  }}
                  className="cursor-pointer group flex items-center justify-between py-3 px-3 rounded-lg transition-all duration-200 focus:bg-theme-primary/10 hover:bg-theme-primary/10 focus:outline-none"
                  style={{
                    background: isSelected 
                      ? `rgba(var(--theme-primary), 0.15)` 
                      : 'transparent'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="relative w-6 h-6 rounded-full border-2 shadow-md transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 flex-shrink-0"
                      style={{
                        background: `linear-gradient(135deg, rgb(${theme.primary}), rgb(${theme.secondary}))`,
                        borderColor: isSelected ? `rgb(${theme.primary})` : 'rgba(128, 128, 128, 0.3)'
                      }}
                    >
                      {isSelected && (
                        <div className="absolute inset-0 rounded-full animate-ping opacity-30"
                          style={{
                            background: `linear-gradient(135deg, rgb(${theme.primary}), rgb(${theme.secondary}))`
                          }}
                        />
                      )}
                    </div>
                    <span 
                      className={`text-sm font-medium transition-colors ${
                        isSelected ? 'font-semibold' : ''
                      }`}
                      style={{
                        color: isDarkMode ? '#ffffff' : '#1a1a1a'
                      }}
                    >
                      {theme.name}
                    </span>
                  </div>
                  {isSelected && (
                    <Check 
                      className="h-4 w-4 animate-in zoom-in duration-200 flex-shrink-0 text-theme-primary" 
                    />
                  )}
                </DropdownMenuItem>
              )
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu> */}

      {/* Dark/Light Mode Toggle */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleDarkMode}
        className="relative h-9 w-9 p-0 rounded-xl transition-all duration-300 hover:scale-110 group overflow-hidden border border-transparent hover:border-theme-primary/20"
        aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
      >
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
          style={{
            background: isDarkMode 
              ? `linear-gradient(135deg, rgb(var(--theme-accent)), rgb(var(--theme-secondary)))`
              : `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-accent)))`
          }}
        />
        <div className="relative z-10">
          {isDarkMode ? (
            <Sun 
              className="h-4 w-4 transition-all duration-500 rotate-0 group-hover:rotate-180 text-theme-accent" 
            />
          ) : (
            <Moon 
              className="h-4 w-4 transition-all duration-500 rotate-0 group-hover:-rotate-12 text-theme-primary" 
            />
          )}
        </div>
      </Button>
    </div>
  )
}
